/**
 * AppLayout - 共享布局组件
 * 用于 Electron 和 Web 两端的主布局
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Dropdown, Avatar, Tag, Spin, Slider } from 'antd';
import { SettingOutlined, LogoutOutlined, UserOutlined, BulbOutlined, AppstoreOutlined } from '@ant-design/icons';
import type { Icon } from '@phosphor-icons/react';
import { CompassIcon, RssIcon, AcornIcon, CloudIcon } from '@phosphor-icons/react';
import { useThemeMode } from './ThemeProvider';
import { useGridDensity, type GridDensityLevel } from '../contexts/GridDensityContext';
import { NotificationCenter } from './NotificationCenter';
import avatarPlaceholder from '../assets/images/avatar-placeholder.jpg';

const { Sider } = Layout;

/** 导航项配置 */
export interface NavItem {
  key: string;
  label: string;
  path: string;
  icon: Icon;
}

/** 平台类型 */
export type AppPlatform = 'electron' | 'web';

/** 所有导航项定义 */
const ALL_NAV_ITEMS: NavItem[] = [
  { key: 'square', label: '广场', path: '/square', icon: CompassIcon },
  { key: 'subscriptions', label: '订阅', path: '/subscriptions', icon: RssIcon },
  { key: 'cloud-clippings', label: '云剪藏簿', path: '/cloud-clippings', icon: CloudIcon },
  { key: 'my', label: '剪藏簿', path: '/my', icon: AcornIcon },
];

/** 各平台显示的导航项 key */
const PLATFORM_NAV_KEYS: Record<AppPlatform, string[]> = {
  electron: ['square', 'subscriptions', 'cloud-clippings', 'my'],
  web: ['square', 'subscriptions', 'cloud-clippings'],
};

/** 根据平台获取导航项 */
const getNavItemsForPlatform = (platform: AppPlatform): NavItem[] => {
  const keys = PLATFORM_NAV_KEYS[platform];
  return ALL_NAV_ITEMS.filter(item => keys.includes(item.key));
};

/** 用户信息 */
export interface AppLayoutUser {
  username?: string;
  email?: string;
  avatarUrl?: string;
}

/** AppLayout 组件 Props */
export interface AppLayoutProps {
  /** 平台类型（用于决定显示哪些导航项） */
  platform: AppPlatform;
  /** 自定义导航项配置（可选，覆盖默认） */
  navItems?: NavItem[];
  /** 当前用户信息 */
  user?: AppLayoutUser | null;
  /** 用户组标签文字 */
  userGroup?: string;
  /** 是否已登录 */
  isLoggedIn: boolean;
  /** 是否正在初始化（显示 loading） */
  isInitializing?: boolean;
  /** 退出登录回调 */
  onLogout?: () => Promise<void>;
  /** 是否记住每个 tab 的路径（切换时恢复） */
  usePathMemory?: boolean;
  /** 设置弹窗组件 */
  SettingsModal: React.ComponentType<{ visible: boolean; onClose: () => void }>;
  /** 默认路径（未登录时的默认跳转） */
  defaultPath?: string;
  /** 自定义 CSS 类名 */
  className?: string;
}

const AppLayout: React.FC<AppLayoutProps> = ({
  platform,
  navItems: customNavItems,
  user,
  userGroup = 'FREE',
  isLoggedIn,
  isInitializing = false,
  onLogout,
  usePathMemory = false,
  SettingsModal,
  defaultPath = '/square',
  className,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, toggle } = useThemeMode();
  const { density, setDensity } = useGridDensity();
  const [settingsVisible, setSettingsVisible] = useState(false);

  // 根据平台获取导航项（可被自定义覆盖，使用 useMemo 避免重复创建）
  const navItems = useMemo(
    () => customNavItems || getNavItemsForPlatform(platform),
    [customNavItems, platform]
  );

  // 记住每个 tab 的最后访问路径（可选功能）
  const lastPathsRef = useRef<Record<string, string>>({});
  const initializedRef = useRef(false);

  // 初始化 lastPathsRef（只在首次挂载时执行）
  useEffect(() => {
    if (!initializedRef.current) {
      const initial: Record<string, string> = {};
      navItems.forEach(item => {
        initial[item.key] = item.path;
      });
      lastPathsRef.current = initial;
      initializedRef.current = true;
    }
  }, [navItems]);

  // 从路径推断当前视图
  const getCurrentView = (): string => {
    // 如果是根路径，不匹配任何视图
    if (location.pathname === '/') {
      return '';
    }

    // 按路径长度降序排序，确保更具体的路径优先匹配
    const sortedItems = [...navItems].sort((a, b) => b.path.length - a.path.length);
    for (const item of sortedItems) {
      if (location.pathname.startsWith(item.path)) {
        return item.key;
      }
    }
    return navItems[0]?.key || '';
  };

  const currentView = getCurrentView();

  // 更新当前 tab 的最后访问路径
  useEffect(() => {
    // 只有在有有效的 currentView 且路径不是根路径时才更新
    if (usePathMemory && currentView && location.pathname !== '/') {
      lastPathsRef.current[currentView] = location.pathname;
    }
  }, [location.pathname, currentView, usePathMemory]);

  // 处理导航点击
  const handleNavClick = (item: NavItem) => {
    if (usePathMemory) {
      const lastPath = lastPathsRef.current[item.key] || item.path;
      navigate(lastPath);
    } else {
      navigate(item.path);
    }
  };

  // 重定向到登录页
  const redirectToAuth = (page: 'login' | 'register' = 'login') => {
    const currentPath = `${location.pathname}${location.search}`;
    const params = new URLSearchParams();
    if (!currentPath.startsWith('/auth')) {
      params.set('redirect', currentPath);
    }
    const targetPath = page === 'login' ? '/auth/login' : '/auth/register';
    const query = params.toString();
    navigate(query ? `${targetPath}?${query}` : targetPath);
  };

  // 通用图标颜色
  const getIconColor = (key: string) =>
    currentView === key ? 'var(--ant-color-text-heading)' : 'var(--ant-color-text-tertiary)';

  // 退出登录处理
  const handleLogout = async () => {
    if (onLogout) {
      await onLogout();
    }
    navigate(defaultPath);
  };

  // 用户菜单项
  const userMenuItems = [
    {
      key: 'account',
      label: isLoggedIn ? (
        <div style={{ padding: '4px 0', cursor: 'default' }}>
          <div style={{ fontWeight: 500, marginBottom: '2px', color: 'var(--ant-color-text)' }}>
            {user?.username || '用户'}
            <Tag style={{ marginLeft: '8px', border: 'none', background: 'var(--ant-color-fill-secondary)', color: 'var(--ant-color-text-secondary)' }}>
              {userGroup}
            </Tag>
          </div>
          <div style={{ fontSize: '12px', color: 'var(--ant-color-text-tertiary)' }}>
            {user?.email}
          </div>
        </div>
      ) : '登录 / 注册',
      onClick: isLoggedIn ? undefined : () => redirectToAuth('login'),
      disabled: isLoggedIn,
      icon: isLoggedIn ? undefined : <UserOutlined />,
      style: isLoggedIn ? { cursor: 'default' } : undefined,
    },
    { type: 'divider' as const },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: '设置',
      onClick: () => setSettingsVisible(true),
    },
    {
      key: 'theme',
      icon: <BulbOutlined />,
      label: '深浅模式',
      children: [
        {
          key: 'light',
          label: '浅色模式',
          icon: <span role="img" aria-label="sun">☀️</span>,
          onClick: () => { if (isDark) toggle(); },
        },
        {
          key: 'dark',
          label: '深色模式',
          icon: <span role="img" aria-label="moon">🌙</span>,
          onClick: () => { if (!isDark) toggle(); },
        },
      ],
    },
    {
      key: 'density',
      icon: <AppstoreOutlined />,
      label: (
        <div
          style={{
            display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', gap: 12
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <span>显示密度</span>
          <Slider
            min={1}
            max={3}
            step={1}
            value={density}
            onChange={(value) => setDensity(value as GridDensityLevel)}
            tooltip={{ formatter: (v) => v === 1 ? '紧凑' : v === 2 ? '标准' : '宽松' }}
            style={{ width: 64, margin: 0 }}
          />
        </div>
      ),
    },
    ...(isLoggedIn ? [
      { type: 'divider' as const },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: '退出登录',
        danger: true,
        onClick: handleLogout,
      },
    ] : []),
  ];

  // 加载中状态
  if (isInitializing) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Layout style={{ height: '100vh' }} className={className}>
      {/* macOS 窗口顶部拖拽区域（横跨整个窗口宽度） */}
      <div
        className="window-drag-region"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 20,
          zIndex: 1000,
        }}
      />

      {/* 一级导航侧栏 */}
      <Sider
        width={64}
        style={{
          // background: 'var(--ant-color-bg-container)',
          borderRight: '1px solid var(--custom-border)',
          // margin: 8,
          // borderRadius: 12,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: '100%',
            paddingTop: 0,
            paddingBottom: 20,
            alignItems: 'center',
          }}
        >
          {/* 顶部导航 Tab 组 */}
          <div
            style={{
              marginTop: 24,
              display: 'flex',
              flexDirection: 'column',
              gap: 14,
              width: '100%',
              alignItems: 'center',
              flex: 1,
            }}
          >
            {navItems.map((item) => {
              const IconComponent = item.icon;
              const isActive = currentView === item.key;

              return (
                <div
                  key={item.key}
                  onClick={() => handleNavClick(item)}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 4,
                    cursor: 'pointer',
                    padding: '8px 0',
                    width: 52,
                    borderRadius: 8,
                    backgroundColor: 'transparent',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--ant-color-fill-tertiary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <IconComponent
                    size={24}
                    color={getIconColor(item.key)}
                    weight={isActive ? 'fill' : 'regular'}
                  />
                  <span style={{
                    fontSize: 11,
                    color: isActive ? 'var(--ant-color-text-heading)' : 'var(--ant-color-text-tertiary)'
                  }}>
                    {item.label}
                  </span>
                </div>
              );
            })}
          </div>

          {/* 底部：通知 + 用户头像 */}
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            {/* 通知中心 */}
            <NotificationCenter
              isLoggedIn={isLoggedIn}
              onNotificationClick={(notification) => {
                console.log('[NotificationCenter] Clicked notification:', {
                  id: notification.id,
                  cloudPostId: notification.cloudPostId,
                  sharedTagGroupId: notification.sharedTagGroupId,
                  sharedTagId: notification.sharedTagId,
                  cloudTagName: notification.cloudTagName,
                });

                // 构建跳转 URL（使用 cloud- 前缀，因为现在存储的是 CloudTagGroup/CloudTag ID）
                let targetPath = '/square';
                if (notification.sharedTagId) {
                  // 独立分享的 tag（暂时使用旧版路由，后续可改为 cloud-tag-）
                  targetPath = `/square/tag-${notification.sharedTagId}`;
                } else if (notification.sharedTagGroupId) {
                  if (notification.cloudTagName) {
                    // tagGroup 下的 cloudTag
                    targetPath = `/square/cloud-taggroup-${notification.sharedTagGroupId}-tag-${encodeURIComponent(notification.cloudTagName)}`;
                  } else {
                    // tagGroup 级别
                    targetPath = `/square/cloud-taggroup-${notification.sharedTagGroupId}`;
                  }
                }

                // 添加高亮帖子参数
                const url = `${targetPath}?highlightPost=${notification.cloudPostId}`;
                console.log('[NotificationCenter] Navigating to:', url);
                navigate(url);
              }}
            />

            {/* 用户头像入口 */}
            <Dropdown
              menu={{ items: userMenuItems, style: { width: 200 } }}
              placement="topRight"
              trigger={['click']}
            >
              <div className="avatar-trigger">
                <Avatar
                  size={36}
                  src={isLoggedIn ? (user?.avatarUrl || avatarPlaceholder) : avatarPlaceholder}
                  icon={!isLoggedIn && <UserOutlined />}
                  style={{ cursor: 'pointer' }}
                />
              </div>
            </Dropdown>
          </div>
        </div>
      </Sider>

      {/* 主内容区 */}
      <Layout style={{ height: '100vh', overflow: 'hidden' }}>
        <Outlet />
      </Layout>

      {/* 设置弹窗 */}
      <SettingsModal
        visible={settingsVisible}
        onClose={() => setSettingsVisible(false)}
      />
    </Layout>
  );
};

export default AppLayout;
